import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  Award, Download, Trophy, TrendingUp, FileText, CheckCircle,
  XCircle, Target, Medal, Star, ArrowLeft, BarChart3
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/contexts/LanguageContext";
import { toast } from "sonner";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";

interface AssessmentResult {
  id: string;
  user_id: string;
  assessment_id: string;
  module_id?: string;
  score: number;
  passed: boolean;
  completed_at: string;
  certificate_url?: string;
  time_taken?: number;
  assessment_forms?: {
    title: string;
    passing_score: number;
  };
  profiles?: {
    full_name: string;
    email: string;
  };
}

interface TopPerformer {
  user?: {
    full_name: string;
    email: string;
  };
  totalScore: number;
  count: number;
  passed: number;
  averageScore: number;
}

interface Statistics {
  totalAssessments: number;
  averageScore: number;
  passRate: number;
  totalCertificates: number;
  recentResults: AssessmentResult[];
  topPerformers: TopPerformer[];
}

export const TrainingAnalytics = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [statistics, setStatistics] = useState<Statistics>({
    totalAssessments: 0,
    averageScore: 0,
    passRate: 0,
    totalCertificates: 0,
    recentResults: [],
    topPerformers: []
  });
  const [selectedPeriod, setSelectedPeriod] = useState('all');

  useEffect(() => {
    fetchAnalytics();
  }, [selectedPeriod]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      
      let query = supabase
        .from('assessment_results')
        .select('*');
      
      if (selectedPeriod !== 'all') {
        const date = new Date();
        switch (selectedPeriod) {
          case 'week':
            date.setDate(date.getDate() - 7);
            break;
          case 'month':
            date.setMonth(date.getMonth() - 1);
            break;
          case 'quarter':
            date.setMonth(date.getMonth() - 3);
            break;
        }
        query = query.gte('completed_at', date.toISOString());
      }
      
      const { data: results, error } = await query.order('completed_at', { ascending: false });
      
      if (error) {
        console.error('Error fetching assessment results:', error);
        setLoading(false);
        return;
      }
      
      // Manually fetch related data
      if (results && results.length > 0) {
        const userIds = [...new Set(results.map(r => r.user_id))];
        const formIds = [...new Set(results.map(r => r.form_id))];
        
        // Fetch profiles
        const { data: profiles } = await supabase
          .from('profiles')
          .select('id, full_name, email')
          .in('id', userIds);
        
        // Fetch assessment forms
        const { data: forms } = await supabase
          .from('assessment_forms')
          .select('id, title, passing_score')
          .in('id', formIds);
        
        // Map the data
        const profileMap = new Map(profiles?.map(p => [p.id, p]) || []);
        const formMap = new Map(forms?.map(f => [f.id, f]) || []);
        
        results.forEach(result => {
          result.profiles = profileMap.get(result.user_id);
          result.assessment_forms = formMap.get(result.form_id);
        });
      }
      
      if (!error && results && results.length > 0) {
        const totalScore = results.reduce((sum, r) => sum + (r.percentage_score || 0), 0);
        const passedCount = results.filter(r => r.passed).length;
        const certificateCount = results.filter(r => r.certificate_url).length;
        
        // Group by user for top performers
        const userScores: Record<string, any> = results.reduce((acc: Record<string, any>, r) => {
          if (!acc[r.user_id]) {
            acc[r.user_id] = {
              user: r.profiles,
              totalScore: 0,
              count: 0,
              passed: 0
            };
          }
          acc[r.user_id].totalScore += r.percentage_score || 0;
          acc[r.user_id].count++;
          if (r.passed) acc[r.user_id].passed++;
          return acc;
        }, {});
        
        const topPerformers = Object.values(userScores)
          .map((u: any) => ({
            ...u,
            averageScore: u.totalScore / u.count
          }))
          .sort((a: any, b: any) => b.averageScore - a.averageScore)
          .slice(0, 5) as TopPerformer[];
        
        setStatistics({
          totalAssessments: results.length,
          averageScore: totalScore / results.length,
          passRate: (passedCount / results.length) * 100,
          totalCertificates: certificateCount,
          recentResults: results.slice(0, 10),
          topPerformers
        });
      } else {
        // Empty state
        setStatistics({
          totalAssessments: 0,
          averageScore: 0,
          passRate: 0,
          totalCertificates: 0,
          recentResults: [],
          topPerformers: []
        });
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const downloadCertificate = async (result: AssessmentResult) => {
    if (!result.certificate_url) {
      toast.error('Certificate not available');
      return;
    }
    
    try {
      const { data } = await supabase.storage
        .from('certificates')
        .createSignedUrl(result.certificate_url, 3600);
      
      if (data?.signedUrl) {
        window.open(data.signedUrl, '_blank');
      }
    } catch (error) {
      console.error('Error downloading certificate:', error);
      toast.error('Failed to download certificate');
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 90) return 'text-green-600';
    if (score >= 70) return 'text-blue-600';
    if (score >= 50) return 'text-yellow-600';
    return 'text-red-600';
  };

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/training')}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1">
          <h1 className="text-3xl font-bold">
            {language === 'en' ? 'Training Analytics' : 'Αναλυτικά Εκπαίδευσης'}
          </h1>
          <p className="text-muted-foreground">
            {language === 'en' 
              ? 'Track assessment performance and manage certificates' 
              : 'Παρακολούθηση απόδοσης αξιολογήσεων και διαχείριση πιστοποιητικών'}
          </p>
        </div>
        
        <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Select Period" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Time</SelectItem>
            <SelectItem value="week">Last Week</SelectItem>
            <SelectItem value="month">Last Month</SelectItem>
            <SelectItem value="quarter">Last Quarter</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Statistics Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {language === 'en' ? 'Total Assessments' : 'Σύνολο Αξιολογήσεων'}
            </CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{statistics.totalAssessments}</div>
            <p className="text-xs text-muted-foreground">
              {language === 'en' ? 'Completed assessments' : 'Ολοκληρωμένες αξιολογήσεις'}
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {language === 'en' ? 'Average Score' : 'Μέσος Όρος'}
            </CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${getScoreColor(statistics.averageScore)}`}>
              {statistics.averageScore.toFixed(1)}%
            </div>
            <Progress value={statistics.averageScore} className="mt-2" />
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {language === 'en' ? 'Pass Rate' : 'Ποσοστό Επιτυχίας'}
            </CardTitle>
            <Target className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{statistics.passRate.toFixed(1)}%</div>
            <Progress value={statistics.passRate} className="mt-2" />
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {language === 'en' ? 'Certificates Issued' : 'Πιστοποιητικά'}
            </CardTitle>
            <Award className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{statistics.totalCertificates}</div>
            <p className="text-xs text-muted-foreground">
              {language === 'en' ? 'Total certificates' : 'Συνολικά πιστοποιητικά'}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs */}
      <Tabs defaultValue="results" className="space-y-4">
        <TabsList>
          <TabsTrigger value="results">Recent Results</TabsTrigger>
          <TabsTrigger value="performers">Top Performers</TabsTrigger>
        </TabsList>
        
        <TabsContent value="results">
          <Card>
            <CardHeader>
              <CardTitle>
                {language === 'en' ? 'Recent Assessment Results' : 'Πρόσφατα Αποτελέσματα'}
              </CardTitle>
              <CardDescription>
                {language === 'en' 
                  ? 'Latest completed assessments and scores' 
                  : 'Τελευταίες ολοκληρωμένες αξιολογήσεις και βαθμολογίες'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8 text-muted-foreground">Loading...</div>
              ) : statistics.recentResults.length > 0 ? (
                <div className="space-y-4">
                  {statistics.recentResults.map((result) => (
                    <div key={result.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-accent/50 transition-colors">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{result.profiles?.full_name || 'Unknown'}</span>
                          {result.passed ? (
                            <CheckCircle className="h-4 w-4 text-green-600" />
                          ) : (
                            <XCircle className="h-4 w-4 text-red-600" />
                          )}
                        </div>
                        <span className="text-sm text-muted-foreground">
                          {result.assessment_forms?.title || 'Assessment'} • {format(new Date(result.completed_at), 'PPP')}
                        </span>
                      </div>
                      
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <div className={`text-lg font-bold ${getScoreColor(result.score || 0)}`}>
                            {result.score || 0}%
                          </div>
                          <Badge variant={result.passed ? 'default' : 'destructive'}>
                            {result.passed ? 'Passed' : 'Failed'}
                          </Badge>
                        </div>
                        
                        {result.certificate_url && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => downloadCertificate(result)}
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 space-y-4">
                  <BarChart3 className="h-16 w-16 text-muted-foreground/50 mx-auto" />
                  <div>
                    <h3 className="font-semibold text-lg mb-1">
                      {language === 'en' ? 'No Assessment Data Yet' : 'Δεν υπάρχουν δεδομένα αξιολόγησης'}
                    </h3>
                    <p className="text-muted-foreground">
                      {language === 'en' 
                        ? 'Complete assessments to see analytics here' 
                        : 'Ολοκληρώστε αξιολογήσεις για να δείτε αναλυτικά'}
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="performers">
          <Card>
            <CardHeader>
              <CardTitle>
                {language === 'en' ? 'Top Performers' : 'Κορυφαίοι Εκπαιδευόμενοι'}
              </CardTitle>
              <CardDescription>
                {language === 'en' 
                  ? 'Users with the highest average scores' 
                  : 'Χρήστες με τους υψηλότερους μέσους όρους'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {statistics.topPerformers.length > 0 ? (
                <div className="space-y-4">
                  {statistics.topPerformers.map((performer, index) => (
                    <div key={index} className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex items-center gap-4">
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
                          {index === 0 && <Trophy className="h-5 w-5 text-yellow-600" />}
                          {index === 1 && <Medal className="h-5 w-5 text-gray-400" />}
                          {index === 2 && <Medal className="h-5 w-5 text-orange-600" />}
                          {index > 2 && <span className="font-bold">{index + 1}</span>}
                        </div>
                        <div>
                          <div className="font-medium">{performer.user?.full_name || 'Unknown'}</div>
                          <div className="text-sm text-muted-foreground">
                            {performer.count} {language === 'en' ? 'assessments' : 'αξιολογήσεις'} • 
                            {performer.passed}/{performer.count} {language === 'en' ? 'passed' : 'επιτυχίες'}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className={`text-xl font-bold ${getScoreColor(performer.averageScore)}`}>
                          {performer.averageScore.toFixed(1)}%
                        </div>
                        <div className="flex gap-1">
                          {[...Array(5)].map((_, i) => (
                            <Star
                              key={i}
                              className={`h-4 w-4 ${
                                i < Math.round(performer.averageScore / 20)
                                  ? 'text-yellow-400 fill-current'
                                  : 'text-gray-300'
                              }`}
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  {language === 'en' ? 'No performers yet' : 'Δεν υπάρχουν εκπαιδευόμενοι ακόμη'}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};